import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createVisitaExterna } from '../../Redux/Actions/actions';
import ClientSelector from '../../Components/ClientSelector/ClientSelector';
import style from './VisitaExternaForm.module.css';

const VisitaExternaForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.user);
  const userId = useSelector(state => state.userId);

  const agenteName = user?.user
    ? `${user.user.name || ''} ${user.user.last_name || ''}`.trim()
    : '';

  const [formData, setFormData] = useState({
    visitante: '',
    agente: agenteName,
    direccion: '',
    fecha: new Date().toISOString().split('T')[0],
    localidad: '',
    provincia: '',
    inmobiliariaExterna: '',
    tipoPropiedad: '',
    precio: '',
    moneda: 'USD',
    descripcion: '',
    observaciones: '',
    interes: { yes: false, no: false, maybe: false },
  });

  const [selectedClients, setSelectedClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInteresChange = (key) => {
    setFormData(prev => ({
      ...prev,
      interes: {
        yes: key === 'yes',
        no: key === 'no',
        maybe: key === 'maybe',
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        sellerId: userId ? parseInt(userId) : null,
        clientId: selectedClients.length > 0 ? selectedClients[0].id : null,
      };

      // Si el visitante no se llenó pero hay un cliente seleccionado, usar su nombre
      if (!payload.visitante && selectedClients.length > 0) {
        payload.visitante = `${selectedClients[0].nombre} ${selectedClients[0].apellido}`.trim();
      }

      await dispatch(createVisitaExterna(payload));
      navigate('/anotar-visita');
    } catch (err) {
      setError(err.message || 'Error al crear la visita externa');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={style.container}>
      <h2 className={style.title}>Registrar Visita Externa</h2>
      <p className={style.subtitle}>
        Registrá una visita a una propiedad que NO pertenece a la inmobiliaria
      </p>

      {error && <div className={style.error}>{error}</div>}

      <form onSubmit={handleSubmit} className={style.form}>
        {/* Sección: Datos de la visita */}
        <div className={style.section}>
          <h3 className={style.sectionTitle}>Datos de la visita</h3>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Visitante / Comprador *</label>
              <input
                type="text"
                name="visitante"
                className={style.input}
                value={formData.visitante}
                onChange={handleChange}
                placeholder="Nombre del cliente comprador"
                required
              />
            </div>

            <div className={style.formGroup}>
              <label className={style.label}>Agente *</label>
              <input
                type="text"
                name="agente"
                className={style.input}
                value={formData.agente}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Fecha</label>
              <input
                type="date"
                name="fecha"
                className={style.input}
                value={formData.fecha}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Selector de cliente existente */}
          {userId && (
            <ClientSelector
              selectedClients={selectedClients}
              onSelectionChange={setSelectedClients}
              sellerId={userId}
              showCategoryFilter
              allowCreate
            />
          )}
        </div>

        {/* Sección: Datos de la propiedad externa */}
        <div className={style.section}>
          <h3 className={style.sectionTitle}>Datos de la propiedad externa</h3>

          <div className={style.formGroup}>
            <label className={style.label}>Dirección *</label>
            <input
              type="text"
              name="direccion"
              className={style.input}
              value={formData.direccion}
              onChange={handleChange}
              placeholder="Ej: Av. Corrientes 1234, Piso 5 Dto B"
              required
            />
          </div>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Localidad</label>
              <input
                type="text"
                name="localidad"
                className={style.input}
                value={formData.localidad}
                onChange={handleChange}
                placeholder="Ej: CABA"
              />
            </div>

            <div className={style.formGroup}>
              <label className={style.label}>Provincia</label>
              <input
                type="text"
                name="provincia"
                className={style.input}
                value={formData.provincia}
                onChange={handleChange}
                placeholder="Ej: Buenos Aires"
              />
            </div>
          </div>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Inmobiliaria externa</label>
              <input
                type="text"
                name="inmobiliariaExterna"
                className={style.input}
                value={formData.inmobiliariaExterna}
                onChange={handleChange}
                placeholder="Nombre de la inmobiliaria dueña"
              />
            </div>

            <div className={style.formGroup}>
              <label className={style.label}>Tipo de propiedad</label>
              <select
                name="tipoPropiedad"
                className={style.input}
                value={formData.tipoPropiedad}
                onChange={handleChange}
              >
                <option value="">Seleccionar...</option>
                <option value="Departamento">Departamento</option>
                <option value="Casa">Casa</option>
                <option value="PH">PH</option>
                <option value="Terreno">Terreno</option>
                <option value="Local">Local</option>
                <option value="Oficina">Oficina</option>
                <option value="Galpón">Galpón</option>
                <option value="Campo">Campo</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
          </div>

          <div className={style.row}>
            <div className={style.formGroup}>
              <label className={style.label}>Precio</label>
              <input
                type="text"
                name="precio"
                className={style.input}
                value={formData.precio}
                onChange={handleChange}
                placeholder="Ej: 120000"
              />
            </div>

            <div className={style.formGroup}>
              <label className={style.label}>Moneda</label>
              <select
                name="moneda"
                className={style.input}
                value={formData.moneda}
                onChange={handleChange}
              >
                <option value="USD">USD</option>
                <option value="ARS">ARS</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sección: Feedback */}
        <div className={style.section}>
          <h3 className={style.sectionTitle}>Feedback</h3>

          <fieldset className={style.fieldset}>
            <legend className={style.legend}>¿Le interesó la propiedad al comprador?</legend>
            <div className={style.radioGroup}>
              <label className={style.radioLabel}>
                <input
                  type="radio"
                  name="interes"
                  checked={formData.interes.yes}
                  onChange={() => handleInteresChange('yes')}
                />
                Sí, le interesa
              </label>
              <label className={style.radioLabel}>
                <input
                  type="radio"
                  name="interes"
                  checked={formData.interes.no}
                  onChange={() => handleInteresChange('no')}
                />
                No le interesa
              </label>
              <label className={style.radioLabel}>
                <input
                  type="radio"
                  name="interes"
                  checked={formData.interes.maybe}
                  onChange={() => handleInteresChange('maybe')}
                />
                Quizás / Lo está pensando
              </label>
            </div>
          </fieldset>

          <div className={style.formGroup}>
            <label className={style.label}>Descripción</label>
            <textarea
              name="descripcion"
              className={style.textarea}
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Notas generales de la visita..."
              rows={3}
            />
          </div>

          <div className={style.formGroup}>
            <label className={style.label}>Observaciones</label>
            <textarea
              name="observaciones"
              className={style.textarea}
              value={formData.observaciones}
              onChange={handleChange}
              placeholder="Observaciones adicionales..."
              rows={3}
            />
          </div>
        </div>

        <div className={style.actions}>
          <button
            type="button"
            className={style.cancelButton}
            onClick={() => navigate('/anotar-visita')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={style.submitButton}
            disabled={submitting}
          >
            {submitting ? 'Registrando...' : 'Registrar Visita Externa'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VisitaExternaForm;
